import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import UserProfile from '../UserProfile';
import {
  LayoutDashboard,
  Users,
  Building2,
  Mail,
  ScrollText,
  BarChart3,
  Shield,
  Menu,
  X,
  ChevronRight,
} from 'lucide-react';
import { STAFF_ROLE_LABELS } from '../../types/staffPlatform';
import { Link } from 'react-router-dom';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  requiresManageUsers?: boolean;
  requiresManageSettings?: boolean;
}

const navItems: NavItem[] = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard, path: '/staff/dashboard' },
  { id: 'vendors', label: 'Vendors', icon: Building2, path: '/staff/dashboard/vendors' },
  { id: 'users', label: 'User Management', icon: Users, path: '/staff/dashboard/users', requiresManageUsers: true },
  { id: 'notifications', label: 'Email Notifications', icon: Mail, path: '/staff/dashboard/notifications', requiresManageSettings: true },
  { id: 'audit', label: 'Audit Log', icon: ScrollText, path: '/staff/dashboard/audit-log' },
  { id: 'reports', label: 'Reports', icon: BarChart3, path: '/staff/dashboard/reports' },
];

const StaffLayout: React.FC = () => {
  const location = useLocation();
  const { staffProfile, canManageUsers, canManageSettings, staffLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const visibleNavItems = navItems.filter((item) => {
    if (item.requiresManageUsers && !canManageUsers) return false;
    if (item.requiresManageSettings && !canManageSettings) return false;
    return true;
  });

  const isActive = (path: string) => {
    if (path === '/staff/dashboard/vendors') {
      return location.pathname.startsWith('/staff/vendor') || location.pathname.startsWith(path);
    }
    if (path === '/staff/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const currentPage = visibleNavItems.find((item) => isActive(item.path));
  const pageTitle = location.pathname.startsWith('/staff/vendor/')
    ? 'Vendor Details'
    : currentPage?.label || 'Staff Portal';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-gray-900/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed top-0 left-0 z-30 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="SGC" className="h-9 w-9 object-contain" />
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">Vendor Management</h1>
                <p className="text-xs text-gray-500">Staff Portal</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-gray-500 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-sunbeth-orange-50 text-sunbeth-orange-700 border border-sunbeth-orange-200'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-sunbeth-orange-600' : 'text-gray-400'}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {staffProfile && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-gray-50">
                <Shield className="h-4 w-4 text-sunbeth-orange-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{staffProfile.name}</p>
                  <p className="text-xs text-gray-500 truncate">{STAFF_ROLE_LABELS[staffProfile.role]}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {pageTitle}
                </h2>
                <div className="hidden sm:flex items-center text-xs text-gray-500 gap-1">
                  <span>Staff</span>
                  <ChevronRight className="h-3 w-3" />
                  <span>{pageTitle}</span>
                </div>
              </div>
            </div>
            <UserProfile />
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-auto text-left">
          {staffLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
};

export default StaffLayout;
